import React, { useState } from 'react';
import AttachmentUpload from './AttachmentUpload';
import CommonOptionFields from './CommonOptionFields';

const InsuranceForm = ({ onSave, onCancel, initialData }) => {
  const [formData, setFormData] = useState({
    title: initialData?.title || '',
    insuranceType: initialData?.insuranceType || '',
    provider: initialData?.provider || '',
    policyNumber: initialData?.policyNumber || '',
    policyDetails: initialData?.policyDetails || '',
    coverageAmounts: initialData?.coverageAmounts || '',
    deductibles: initialData?.deductibles || '',
    beneficiaries: initialData?.beneficiaries || '',
    providerPhone: initialData?.providerPhone || '',
    policyStartDate: initialData?.policyStartDate || '',
    policyEndDate: initialData?.policyEndDate || '',
    additionalNotes: initialData?.additionalNotes || '',
    premium: initialData?.premium || initialData?.price || '',
    isOption: initialData?.isOption || false,
    optionDescription: initialData?.optionDescription || '',
    paymentTiming: initialData?.paymentTiming || 'with_deposit',
    optionalDepositAmount: initialData?.optionalDepositAmount || '',
    agentNotes: initialData?.agentNotes || '',
    agentCommission: initialData?.agentCommission ?? null,
    photos: initialData?.photos || [],
    documents: initialData?.documents || []
  });

  const insuranceTypes = [
    'Trip Cancellation',
    'Medical Coverage',
    'Baggage Protection',
    'Flight Accident',
    'Rental Car Protection',
    'Comprehensive Travel Insurance',
    'Business Travel Insurance',
    'Adventure Sports Coverage',
    'Cancel For Any Reason',
    'Other Coverage'
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      type: 'insurance',
      price: parseFloat(formData.premium) || 0,
      paymentTiming: formData.paymentTiming,
      optionalDepositAmount: formData.optionalDepositAmount ? parseFloat(formData.optionalDepositAmount) : null
    });
  };

  return (
    <form onSubmit={handleSubmit} className="component-form insurance-form">
      <div className="form-section">
        <h4>Insurance Information</h4>
        
        <div className="form-group">
          <label>Title/Description*</label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({...formData, title: e.target.value})}
            placeholder="e.g., Comprehensive Travel Insurance - Gold Plan"
            required
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Insurance Type/Coverage*</label>
            <select
              value={formData.insuranceType}
              onChange={(e) => setFormData({...formData, insuranceType: e.target.value})}
              required
            >
              <option value="">Select Coverage Type</option>
              {insuranceTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Insurance Provider*</label>
            <input
              type="text"
              value={formData.provider}
              onChange={(e) => setFormData({...formData, provider: e.target.value})}
              placeholder="e.g., Allianz, World Nomads, Travel Guard"
              required
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h4>Policy Details</h4>
        
        <div className="form-row">
          <div className="form-group">
            <label>Policy Number</label>
            <input
              type="text"
              value={formData.policyNumber}
              onChange={(e) => setFormData({...formData, policyNumber: e.target.value})}
              placeholder="e.g., POL-123456789"
            />
          </div>
          <div className="form-group">
            <label>Provider Phone</label>
            <input
              type="tel"
              value={formData.providerPhone}
              onChange={(e) => setFormData({...formData, providerPhone: e.target.value})}
              placeholder="e.g., (800) 123-4567"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Policy Start Date*</label>
            <input
              type="date"
              value={formData.policyStartDate}
              onChange={(e) => setFormData({...formData, policyStartDate: e.target.value})}
              required
            />
          </div>
          <div className="form-group">
            <label>Policy End Date*</label>
            <input
              type="date"
              value={formData.policyEndDate}
              onChange={(e) => setFormData({...formData, policyEndDate: e.target.value})}
              required
            />
          </div>
        </div>

        <div className="form-group">
          <label>Policy Details*</label>
          <textarea
            value={formData.policyDetails}
            onChange={(e) => setFormData({...formData, policyDetails: e.target.value})}
            placeholder="Describe what this policy covers, key features, etc..."
            rows="3"
            required
          />
        </div>
      </div>

      <div className="form-section">
        <h4>Coverage & Financial Details</h4>
        
        <div className="form-group">
          <label>Coverage Amounts*</label>
          <textarea
            value={formData.coverageAmounts}
            onChange={(e) => setFormData({...formData, coverageAmounts: e.target.value})}
            placeholder="e.g., Trip Cancellation: $10,000&#10;Medical: $50,000&#10;Baggage: $2,500&#10;Emergency Evacuation: $100,000"
            rows="4"
            required
          />
        </div>

        <div className="form-group">
          <label>Deductibles</label>
          <textarea
            value={formData.deductibles}
            onChange={(e) => setFormData({...formData, deductibles: e.target.value})}
            placeholder="e.g., $100 per claim&#10;$0 for medical emergencies&#10;$50 for baggage claims"
            rows="3"
          />
        </div>

        <div className="form-group">
          <label>Beneficiaries</label>
          <textarea
            value={formData.beneficiaries}
            onChange={(e) => setFormData({...formData, beneficiaries: e.target.value})}
            placeholder="Primary: Name, Relationship&#10;Secondary: Name, Relationship"
            rows="2"
          />
        </div>

        <div className="form-group">
          <label>Insurance Premium*</label>
          <input
            type="number"
            value={formData.premium}
            onChange={(e) => setFormData({...formData, premium: e.target.value})}
            placeholder="0.00"
            min="0"
            step="0.01"
            required
          />
        </div>
      </div>

      <div className="form-section">
        <h4>Additional Information</h4>
        
        <div className="form-group">
          <label>Additional Notes</label>
          <textarea
            value={formData.additionalNotes}
            onChange={(e) => setFormData({...formData, additionalNotes: e.target.value})}
            placeholder="Important exclusions, claim procedures, emergency contacts, pre-existing condition coverage, etc..."
            rows="4"
          />
        </div>
      </div>

      <CommonOptionFields formData={formData} setFormData={setFormData} />

      <AttachmentUpload
        photos={formData.photos}
        documents={formData.documents}
        onPhotosChange={(photos) => setFormData({...formData, photos})}
        onDocumentsChange={(documents) => setFormData({...formData, documents})}
        maxPhotos={5}
        maxDocuments={3}
        showPhotos={true}
        showDocuments={true}
      />

      <div className="form-actions">
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary">
          {initialData ? 'Update' : 'Add'} Insurance
        </button>
      </div>
    </form>
  );
};

export default InsuranceForm;
